import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import HeroAnimation from '../components/home/HeroAnimation';
import ChromeWordmark from '../components/home/ChromeWordmark';
import '../components/home/home.css';

const TITLE = 'Active Gaming Controller — Play with your arms & legs | V4LLUED';
const DESCRIPTION = 'Walk, run, and shoot inside your favourite games with real physical movement. V4LLUED Steps and V4LLUED Guns turn your phone and Joy-Con into an active gaming controller.';

export default function Home() {
  const [demoPlaying, setDemoPlaying] = useState(false);

  usePageMeta({
    title: TITLE,
    description: DESCRIPTION,
    ogTitle: 'Active Gaming Controller — V4LLUED',
    ogDescription: 'Walk, run, and shoot inside your favourite games with real physical movement.',
    ogImage: `${window.location.origin}/social_preview_v3.jpg`,
    ogUrl: window.location.origin,
  });

  const focusSensor = (event, id) => {
    event.preventDefault();
    const sensor = document.getElementById(id);
    if (!sensor) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    sensor.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    sensor.focus({ preventScroll: true });
  };

  return (
    <div className="home-page">
      <header className="home-header">
        <div className="home-wrap home-nav">
          <span className="home-logo">
            <ChromeWordmark
              className="chrome-wordmark--brand"
              label="V4LLUED"
              src="/chrome-v4llued-reference.png"
            />
          </span>
          <ul>
            <li><a href="https://linktr.ee/v4llued" target="_self" rel="noopener noreferrer">Resources</a></li>
          </ul>
        </div>
      </header>

      <main id="top">
        <section className="home-hero">
          <div className="home-wrap">
            <p className="home-eyebrow">Active Gaming</p>
            <h1 aria-label="Controller">
              <ChromeWordmark
                className="chrome-wordmark--controller"
                label="Controller"
                src="/chrome-controller-reference.png"
              />
            </h1>
            <p className="home-tagline">Get fit while gaming</p>
          </div>
          <div className="home-wrap home-hero-video">
            <HeroAnimation />
          </div>
          <div className="home-wrap home-hero-bottom">
            <nav className="home-hero-actions" aria-label="Explore V4LLUED">
              <div className="home-sensor-actions">
                <a className="home-button" href="#steps" onClick={(event) => focusSensor(event, 'steps')}>Leg Sensor</a>
                <a className="home-button" href="#guns" onClick={(event) => focusSensor(event, 'guns')}>Arm Sensor</a>
              </div>
              <a className="home-watch-link" href="#demo">Watch demo</a>
            </nav>
          </div>
        </section>

        <section className="home-section" id="how">
          <div className="home-wrap">
            <h2>Use your body to play video games</h2>
            <p className="home-lede">Your legs move your character forward, your arms aim and fire.</p>
            <ol className="home-flow">
              <li>
                <div className="home-step-number">1</div>
                <h3>PUT phone in your pocket</h3>
                <p>Install V4llued Steps on PC/Console and pair it with your iOS or Android phone.</p>
              </li>
              <li>
                <div className="home-step-number">2</div>
                <h3>PUT Joycon into gun shell</h3>
                <p>Apply the V4llued Guns JSM settings on PC. For Consoles enable the gravity alignment in XIM Matrix.</p>
                <p className="home-step-note"><em>(Consoles require ESP32 USB and a XIM Matrix adapter).</em></p>
              </li>
              <li>
                <div className="home-step-number">3</div>
                <h3>That’s it!</h3>
                <p>Get fit while gaming!</p>
              </li>
            </ol>
          </div>
        </section>

        <section className="home-section home-products-section">
          <div className="home-wrap">
            <div className="home-product-cards">
              <article className="home-card" id="steps" tabIndex="-1">
                <h3>V4LLUED Steps</h3>
                <p className="home-role">Lower-body movement</p>
                <ul>
                  <li><strong>Placement:</strong> phone in your pocket.</li>
                  <li><strong>Tech:</strong> sensor tracking turns strides into keybind inputs.</li>
                  <li><strong>Controls:</strong> step in place to walk and run in-game.</li>
                </ul>
                <a className="home-button" href="https://vsteps.io">Start stepping</a>
                <p className="home-setup"><Link to="/Steps">Setup Guide</Link></p>
              </article>
              <article className="home-card" id="guns" tabIndex="-1">
                <h3>V4LLUED Guns</h3>
                <p className="home-role">Upper-body aim and combat</p>
                <ul>
                  <li><strong>Hardware:</strong> 3D-printed joycon housing shells (joycon sold separately).</li>
                  <li><strong>Tech:</strong> gyro aiming built for FPS games.</li>
                  <li><strong>Controls:</strong> aim the reticle and fire with natural arm motion.</li>
                </ul>
                <Link className="home-button" to="/Guns">GET GUN SHELLS</Link>
              </article>
            </div>
          </div>
        </section>

        <section className="home-section home-demo-section" id="demo">
          <div className="home-wrap">
            <h2>See it in action</h2>
            <p className="home-lede">Sedentary gaming and VR radiowaves next to your skull is bad for your Health. Try Active Gaming.</p>
            <div className="home-demo" id="demo-card">
              {demoPlaying ? (
                <iframe
                  src="https://www.youtube-nocookie.com/embed/Ak-DQ77rqUQ?autoplay=1&rel=0&playsinline=1"
                  title="V4LLUED Active Gaming demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <button
                  type="button"
                  className="home-play"
                  aria-label="Play demo video"
                  onClick={() => setDemoPlaying(true)}
                >
                  <svg className="home-play-reticle" viewBox="0 0 112 112" aria-hidden="true">
                    <circle cx="56" cy="56" r="35" />
                    <path d="M56 4v20M56 88v20M4 56h20M88 56h20" />
                    <path d="M56 0v12M56 100v12M0 56h12M100 56h12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="home-footer">
        <div className="home-wrap">
          <a className="home-logo" href="#top">V4LLUED</a>
          <p>Experience seamless walk, run, pedal and shooting in video games! Try it with steering wheels or exercise bikes!</p>
          <small>© 2026 V4llued. All rights reserved.</small>
        </div>
      </footer>
    </div>
  );
}